import { useState, useEffect } from 'react';
import Step from 'components/steps/step';
import Steps from 'components/steps';
import Loading from 'components/center-loading';
import * as S from './styledImportExport';
import intl from 'react-intl-universal';
import IconLog from "images/icn-log.svg";
import { downloadFileTxt } from 'utils/custom-functions';
import IconButton from '@material-ui/core/IconButton';
import { IUploadMasterdata, UploadMasterdata } from './import-export-json.types';
import { createPromises, formatAndValidateFile } from './utils';
import { isFeatureEnabled } from 'utils/feature-flags';
import Step1 from './components/step1';
import Step2 from './components/step2';
import Step3 from './components/step3';
import { IReadFile, Result } from './types';
import LoadingCircularProgress from 'components/loading-circular-progress';
import Button from 'components/button';
import { createVolumeNegotiationPromises, formatAndValidateVolumeNegotiationFile } from './volume-negotiation-utils';
import { UploadVolumeNegotiation } from './types/volume-negotiation.types';

interface IUploadMasterdataItem extends Omit<IUploadFileItem, 'Data'> {
  Data: IUploadMasterdata
}

interface ImportExportJsonProps {
  open: boolean;
  title: string;
  contextModal: string;
  handleClose: () => void;
  downloadArquivo: () => Promise<void>;
  downloadModelo?: () => Promise<void>;
  stepSelected?: number;
  hiddenButtonExport?: boolean;
  noMessageExample?: boolean;
  content?: any;
}

export interface IUploadFile {
  Data: IUploadMasterdataItem[]
}

export interface IUploadFileItem {
  Data: unknown,
}

const ImportExportJson: React.FC<ImportExportJsonProps> = (props) => {
  const [stepSelected, setStepSelected] = useState<number>(props.stepSelected ?? 0);
  const [isMasterDataEnhanced, setIsMasterDataEnhanced] = useState<boolean>(false);
  const [csv, setCsv] = useState<UploadMasterdata[]>([]);
  const [file, setFile] = useState<UploadMasterdata[] | UploadVolumeNegotiation[]>([]);
  const [labelStep3, setLabelStep3] = useState<string>(intl.get(`exportImportModal.${props.contextModal}.step3`));
  const [resultList, setResultList] = useState<Result[]>([]);
  const [importedWithSuccess, setImportedWithSuccess] = useState<boolean>(false);
  const [previousButtonActive, setPreviousButtonActive] = useState<boolean>(true);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [nothingImported, setNothingImported] = useState<boolean>(false);
  const [logsList, setLogsList] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([])
  const [fileName, setFileName] = useState<string>();
  const [isProcessingFile, setIsProcessingFile] = useState<boolean>(false);
  const [processingPercentual, setProcessingPercentual] = useState<number>(0);

  useEffect(() => {
    if (props.contextModal === 'masterData') {
      isFeatureEnabled('FeatureMasterDataEnhancements').then(setIsMasterDataEnhanced);
    }
  }, [props.contextModal]);

  useEffect(() => {
    checkStepToUpdate();
  }, [stepSelected]);

  const handleNext = async () => {
    const promises = props.contextModal !== "volumeNegotiation" ? createPromises(file as UploadMasterdata[], treatSuccess, treatError, setProcessingPercentual, isMasterDataEnhanced) : createVolumeNegotiationPromises(file as UploadVolumeNegotiation[], treatSuccess, treatError, setProcessingPercentual);

    await Promise.all([promises]);
  }

  const handleSuccessMessages = (data: any) => {
    let _data = data;
    const successCounter = _data?.reduce((count: number, x: any) => 
      x.Type === 2 ? count + 1 : count, 0);

    if (successCounter > 0) {
      _data = _data.filter((x: { Type: any }) => x.Type != 2)

      return [
        {
          Type: 2,
          Message: `${intl.get('master-data.general-configuration.totalSuccessMessage')} ${successCounter}`
        },
        ..._data,
      ]
    } else
      return data;
  }

  const treatSuccess = (data: any) => {
    const results = data.map((x: { Results: { Message: string }[] }) => x.Results[0]);
    const allSuccess = data.every((x: { AllSuccess: boolean }) => x.AllSuccess);
    const handledSuccessMessages = handleSuccessMessages(results);

    setLabelStep3(allSuccess && results.length > 0 ?
      intl.get(`exportImportModal.${props.contextModal}.success`) :
      intl.get(`exportImportModal.${props.contextModal}.noSuccess`));
    setResultList(handledSuccessMessages);
    setLogsList(handledSuccessMessages.map((x: {Message: string}) => x.Message));
    setIsProcessingFile(false);
    setProcessingPercentual(0);
    setImportedWithSuccess(allSuccess);
    setPreviousButtonActive(allSuccess ?? (results.length === 0));
    setNothingImported(results.length === 0);
  };

  const treatError = () => {
    setLabelStep3(intl.get(`exportImportModal.${props.contextModal}.noSuccess`));
    setPreviousButtonActive(true);
    setIsProcessingFile(false);
    setProcessingPercentual(0);
    setResultList([{
      Type: 0,
      Message: intl.get(`exportImportModal.${props.contextModal}.noSuccess`),
    }]);
    setLogsList([]);
    setNothingImported(false);
  };

  const checkStepToUpdate = () => {
    if (stepSelected === 2) {
      setLabelStep3(intl.get(`exportImportModal.${props.contextModal}.processing`));
      setIsProcessingFile(true);
      handleNext();
    }
  };

  const changeStepNext = () => {
    setStepSelected(prevStep => prevStep + 1);
  };

  const changeStepPrev = () => {
    setStepSelected(prevStep => prevStep - 1);
    setLabelStep3(intl.get(`exportImportModal.${props.contextModal}.step3`));
    setPreviousButtonActive(true);
    setImportedWithSuccess(false);
    setResultList([]);
    setNothingImported(false);
    setErrors([]);
    setCsv([])
  };

  const close = () => {
    setStepSelected(0);
    setLabelStep3(intl.get(`exportImportModal.${props.contextModal}.step3`));
    setImportedWithSuccess(false);
    setCsv([]);
    setPreviousButtonActive(true);
    setNothingImported(false);
    setResultList([]);
    setLogsList([]);
    setErrors([]);
    props.handleClose();
  };

  const stopFetching = () => {
    setIsFetching(false);
  };

  const startFetching = () => {
    setIsFetching(true);
  };

  const downloadLogsTxt = () => {
    const formatLogs = logsList.map(x => x?.toString()).join('\n');
    downloadFileTxt(`error_log_${fileName}.txt`, formatLogs);
  };

  const { open, title, contextModal, hiddenButtonExport, noMessageExample, content } = props;
  const showResultList = resultList.length > 0;
  

  const handleReadFile = (data: IReadFile) => {
    const _result = contextModal !== "volumeNegotiation" ? formatAndValidateFile(data.data as UploadMasterdata[], isMasterDataEnhanced) : formatAndValidateVolumeNegotiationFile(data.data as UploadVolumeNegotiation[]);
    setErrors(_result.validationMessage);
    if (_result.isValid)
      setFile(_result.data);
    else
      setFile([]);
  }

  return (
    contextModal &&
    <S.ImportExportModal
      bodyClassName='import-export__modal'
      title={title}
      contentStyle={{ width: '700px' }}
      open={open}
    >
      <S.StyledImportExport>
        <Loading isLoading={isFetching} fullHeightParent />
        <LoadingCircularProgress isLoading={isProcessingFile} percentual={processingPercentual} fullHeightParent supportText={intl.get(`exportImportModal.${contextModal}.messageWaitImport`)} />
        <Steps
          blockedSteps={true}
          selected={stepSelected}
        >
          <Step completed={stepSelected > 0} label={intl.get(`exportImportModal.${contextModal}.step1`)}>
            <Step1 
              contextModal={contextModal} 
              content={content} 
              downloadFile={props.downloadArquivo}
              hiddenButtonExport={hiddenButtonExport}
              startFetching={startFetching}
              stopFetching={stopFetching}
            />
          </Step>
          <Step completed={stepSelected > 1} label={intl.get(`exportImportModal.${contextModal}.step2`)}>
            <Step2 
              errors={errors}
              contextModal={contextModal}
              noMessageExample={noMessageExample}
              downloadModelFile={props.downloadModelo}
              startFetching={startFetching}
              stopFetching={stopFetching}
              handleReadFile={handleReadFile}
              setCsv={setCsv}
              setErrors={setErrors}
              setFileName={setFileName}
            />
          </Step>
          <Step completed={stepSelected > 2} label={labelStep3}>
            <Step3 
              contextModal={contextModal}
              showResultList={showResultList}
              nothingImported={nothingImported}
              importedWithSuccess={importedWithSuccess}
              resultList={resultList}
            />
          </Step>
        </Steps>
        <div className="content-footer" data-testid='content-footer'>
          <Button
            onClick={close}
            className="button-footer"
            buttonType="default"
            value={intl.get(`geral.buttonsDefault.close`)}
          />
          <div className="footer-right">
            {showResultList
              && !importedWithSuccess
              && contextModal === 'masterData'
              && <IconButton
                title={intl.get(`exportImportModal.butttons.downloadLogs`)}
                onClick={downloadLogsTxt}
                style={{ height: '40px', width: '40px', padding: '0px', marginRight: '8px' }}>
                <img src={IconLog} className="button-footer btn-dowload-logs" alt="" />
              </IconButton>
            }
            {stepSelected > 0 && previousButtonActive &&
              <Button
                onClick={changeStepPrev}
                className="button-footer"
                buttonType="primary"
                value={intl.get(`geral.buttonsDefault.previous`)}
              />}

            {stepSelected < 2 && <span data-testid='btn-next-import-export'>
              <Button
                onClick={changeStepNext}
                className="button-footer btn-next"
                buttonType="primary"
                value={intl.get(`geral.buttonsDefault.next`)}
                disabled={stepSelected === 1 && !csv.length || errors.length > 0}
              />
            </span>}
          </div>
        </div>
      </S.StyledImportExport>
    </S.ImportExportModal>
  );
};

export default ImportExportJson;
